import type { NextRequest } from 'next/server';

/**
 * Google Ads click-ID capture for conversion attribution.
 *
 * gclid (and gbraid/wbraid from iOS traffic) arrive as URL params when someone
 * lands from an ad click. We persist them in first-party cookies so the
 * checkout routes can stamp them onto the Stripe session metadata — which is
 * what makes offline/server-side conversion uploads to Google Ads possible,
 * independent of whether the browser ever fired the client-side gtag event.
 */

const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;
const COOKIE_PREFIX = 'yt_';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60; // Google Ads click-through window

/** Client-side: call once on app mount. */
export function captureClickIds() {
  if (typeof window === 'undefined') return;
  // Respect an explicit opt-out (the EU consent banner sets this on decline).
  if (localStorage.getItem('cookie-consent') === 'declined') return;

  const params = new URLSearchParams(window.location.search);
  for (const key of CLICK_ID_PARAMS) {
    const value = params.get(key);
    if (value && /^[\w.~-]{1,200}$/.test(value)) {
      document.cookie = `${COOKIE_PREFIX}${key}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax; Secure`;
    }
  }
}

/**
 * Server-side: read captured click IDs off the request so they can be attached
 * to Stripe checkout metadata. Returns only the keys that are present.
 */
export function getAdsClickIds(request: NextRequest): Record<string, string> {
  const ids: Record<string, string> = {};
  for (const key of CLICK_ID_PARAMS) {
    const value = request.cookies.get(`${COOKIE_PREFIX}${key}`)?.value;
    if (value && /^[\w.~-]{1,200}$/.test(value)) {
      ids[key] = value;
    }
  }
  return ids;
}
